import { DirectionProvider as RadixDirectionProvider } from '@radix-ui/react-direction';
import { createContext, useContext, type ReactNode } from 'react';

import { useDirectionProviderLogic, type DirectionState } from './useDirectionProviderLogic';

const DirectionContext = createContext<DirectionState | null>(null);

/**
 * Publishes the active direction to React, and to Radix.
 *
 * Radix needs telling separately: its floating primitives compute a PHYSICAL
 * side (a dropdown aligned `start` has to become `left` or `right` at some
 * point), and it reads that from its own `DirectionProvider` rather than from
 * the `dir` attribute. Without this, every menu, popover and select in the kit
 * would align to the left in an Arabic layout — the single most visible RTL bug
 * a Radix-based admin can ship (U7).
 */
export function DirectionProvider({ children }: { children: ReactNode }): ReactNode {
  const state = useDirectionProviderLogic();

  return (
    <DirectionContext.Provider value={state}>
      <RadixDirectionProvider dir={state.direction}>{children}</RadixDirectionProvider>
    </DirectionContext.Provider>
  );
}

export function useDirectionContext(): DirectionState | null {
  return useContext(DirectionContext);
}
